import type { Attribute } from "@akashnetwork/chain-sdk/private-types/akash.v1";
import type { GroupSpec } from "@akashnetwork/chain-sdk/private-types/akash.v1beta4";
import type { Manifest as AkashManifest, SDLInput } from "@akashnetwork/chain-sdk/web";
import { generateManifestVersion } from "@akashnetwork/chain-sdk/web";
import yaml from "js-yaml";

import { browserEnvConfig } from "@src/config/browser-env.config";
import type { DepositParams } from "@src/types/deployment";
import { buildManifest, CustomValidationError, Manifest, ManifestVersion, parseSdlInput } from "./helpers";

export const ENDPOINT_NAME_VALIDATION_REGEX = /^[a-z]+[-_\da-z]+$/;
export const TRIAL_ATTRIBUTE = "console/trials";
export const TRIAL_REGISTERED_ATTRIBUTE = "console/trials-registered";
export const AUDITOR = "akash1365yvmc4s7awdyj3n2sav7xfx76adc6dnmlx63";
// 自托管网络:托管钱包部署不强制外部 akash 审计员(akash1365yvmc4...)签名。
// 我们的 provider 没被该外部审计员签过,留着会让 signedBy.anyOf 含它 → provider 永不出价。
// 单 provider 自托管不需要审计门槛,留空即可。
export const MANAGED_WALLET_ALLOWED_AUDITORS: string[] = [];

export function getManifest(yamlJson: any): AkashManifest {
  return Manifest(yamlJson);
}

export async function getManifestVersion(yamlJson: any) {
  const version = await ManifestVersion(yamlJson);

  return Buffer.from(version).toString("base64");
}

const getDenomFromSdl = (groups: GroupSpec[]): string => {
  const denoms = groups.flatMap(g => g.resources).map(resource => resource.price?.denom);

  // TODO handle multiple denoms in an sdl? (different denom for each service?)
  return denoms.find(d => !!d)!;
};

export function appendTrialAttribute(yamlStr: string, attributeKey: string) {
  const sdlData = yaml.load(yamlStr) as SDLInput;
  const placementData = sdlData?.profiles?.placement || {};

  for (const [, value] of Object.entries(placementData)) {
    if (!value.attributes) {
      value.attributes = {};
    } else if (Array.isArray(value.attributes)) {
      value.attributes = (value.attributes as unknown as Attribute[]).reduce<Record<string, unknown>>((acc, curr) => ((acc[curr.key] = curr.value), acc), {});
    }

    const attrs = value.attributes as Record<string, unknown>;
    if (!(attributeKey in attrs)) {
      attrs[attributeKey] = "true";
    }

    if (!value.signedBy?.anyOf || !value.signedBy?.allOf) {
      value.signedBy = {
        anyOf: value.signedBy?.anyOf || [],
        allOf: value.signedBy?.allOf || []
      };
    }

    if (value?.signedBy?.allOf && !value.signedBy.allOf.includes(AUDITOR)) {
      value.signedBy.allOf.push(AUDITOR);
    }
  }

  const result = yaml.dump(sdlData, {
    indent: 2,
    quotingType: '"',
    styles: {
      "!!null": "empty" // dump null as empty value
    }
  });

  return `---
${result}`;
}

export function appendAuditorRequirement(yamlStr: string) {
  const sdlData = yaml.load(yamlStr) as SDLInput;
  const placementData = sdlData?.profiles?.placement || {};

  for (const [, value] of Object.entries(placementData)) {
    if (!value.signedBy?.anyOf || !value.signedBy?.allOf) {
      value.signedBy = {
        anyOf: value.signedBy?.anyOf || [],
        allOf: value.signedBy?.allOf || []
      };
    }

    for (const auditor of MANAGED_WALLET_ALLOWED_AUDITORS) {
      if (value?.signedBy?.anyOf && !value.signedBy.anyOf.includes(auditor)) {
        value.signedBy.anyOf.push(auditor);
      }
    }
  }

  const result = yaml.dump(sdlData, {
    indent: 2,
    quotingType: '"',
    styles: {
      "!!null": "empty"
    }
  });

  return `---
${result}`;
}

export function applyTrialGpuPolicy(
  yamlStr: string,
  blockedModels: readonly string[] = browserEnvConfig.NEXT_PUBLIC_MANAGED_WALLET_TRIAL_BLOCKED_GPU_MODELS
): string {
  const sdlData = yaml.load(yamlStr) as SDLInput;
  const computeProfiles = sdlData?.profiles?.compute;
  if (!computeProfiles || typeof computeProfiles !== "object") return yamlStr;

  const blockedSet = new Set(blockedModels);
  if (blockedSet.size === 0) return yamlStr;
  let mutated = false;

  for (const profile of Object.values(computeProfiles)) {
    const gpu = profile?.resources?.gpu;
    const vendorMap = gpu?.attributes?.vendor as Record<string, Array<{ model?: string; ram?: string; interface?: string }> | null | undefined> | undefined;
    if (!vendorMap) continue;

    for (const vendor of Object.keys(vendorMap)) {
      const models = Array.isArray(vendorMap[vendor]) ? vendorMap[vendor]! : [];
      const allowed = models.filter(entry => entry?.model && !blockedSet.has(`${vendor.toLowerCase()}/${entry.model.toLowerCase()}`));

      if (allowed.length === 0) {
        if (vendorMap[vendor] === null) continue;
        // Empty value = any model from this vendor (caught at CreateLease if blocked).
        vendorMap[vendor] = null;
        mutated = true;
      } else if (allowed.length !== models.length) {
        vendorMap[vendor] = allowed;
        mutated = true;
      }
    }
  }

  if (!mutated) return yamlStr;

  const result = yaml.dump(sdlData, {
    indent: 2,
    quotingType: '"',
    styles: {
      "!!null": "empty"
    }
  });

  return `---
${result}`;
}

export function replaceSdlDenom(yamlStr: string, denom: string): string {
  const sdlData = yaml.load(yamlStr) as SDLInput;
  const placementData = sdlData?.profiles?.placement || {};

  for (const [, placement] of Object.entries(placementData)) {
    const pricing = placement.pricing || {};
    for (const [, price] of Object.entries(pricing)) {
      if (price.denom) {
        price.denom = denom as typeof price.denom;
      }
    }
  }

  const result = yaml.dump(sdlData, {
    indent: 2,
    quotingType: '"',
    styles: {
      "!!null": "empty"
    }
  });

  return `---
${result}`;
}

export async function NewDeploymentData(
  yamlStr: string,
  dseq: string | null,
  fromAddress: string,
  deposit: number | DepositParams[] = browserEnvConfig.NEXT_PUBLIC_DEFAULT_INITIAL_DEPOSIT
) {
  try {
    const sdlInput = parseSdlInput(yamlStr);
    const manifest = buildManifest(sdlInput);
    const groups = manifest.groupSpecs;
    const mani = manifest.groups;
    const denom = getDenomFromSdl(groups);
    const version = await generateManifestVersion(manifest.groups);
    const _deposit = (Array.isArray(deposit) && deposit.find(d => d.denom === denom)) || { denom, amount: deposit.toString() };

    const finalDseq: string = dseq || Date.now().toString();

    return {
      sdl: sdlInput,
      manifest: mani,
      groups: groups,
      deploymentId: {
        owner: fromAddress,
        dseq: finalDseq
      },
      orderId: [],
      leaseId: [],
      hash: version,
      deposit: _deposit,
      reclamation: manifest.reclamation
    };
  } catch (e: any) {
    const error = new CustomValidationError(e.message);
    error.stack = e.stack;
    throw error;
  }
}
